import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { C } from '../../constants';
import { SearchIcon, UserIcon, WhatsAppIcon, PhoneIcon } from '../components/AdminIcons';
import { formatDate } from '../utils/dateUtils';

const ST = { completed:{ bg:'rgba(16,185,129,0.1)', color:'#059669' }, cancelled:{ bg:'rgba(220,38,38,0.1)', color:'#DC2626' }, pending:{ bg:'rgba(245,158,11,0.1)', color:'#D97706' } };

function groupClients(bookings) {
  const map = {};
  bookings.forEach(b => {
    const key = (b.phone||'').replace(/\s/g,'') || b.name;
    if (!map[key]) map[key] = { name:b.name, phone:b.phone, email:b.email, visits:[] };
    map[key].visits.push(b);
  });
  Object.values(map).forEach(c => c.visits.sort((a,b) => !a.date?1:!b.date?-1: new Date(b.date)-new Date(a.date)));
  return Object.values(map).sort((a,b) => b.visits.length - a.visits.length);
}

export default function ClientHistory() {
  const [bookings, setBookings] = useState([]);
  const [search, setSearch]     = useState('');
  const [selKey, setSelKey]     = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'bookings'), orderBy('createdAt','desc'));
    return onSnapshot(q, snap => setBookings(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, []);

  const clients = groupClients(bookings);
  const filtered = clients.filter(c => !search ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const sel = selKey ? filtered.find(c => ((c.phone||'').replace(/\s/g,'')||c.name) === selKey) : null;
  const waPhone = sel?.phone?.replace(/[\s\-\(\)\+]/g,'');
  const waFull  = waPhone?.startsWith('0') ? '233'+waPhone.slice(1) : waPhone;
  const waMsg   = sel ? encodeURIComponent('Hello '+sel.name+', thank you for visiting Eye-Sight Care & Optical! We hope we could serve you well. Feel free to contact us on +233 547 097 213 anytime.') : '';

  return (
    <div className="admin-page" style={{ padding:32 }}>
      <h1 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:'2rem', fontWeight:700, color:'#0B1D3A', marginBottom:4 }}>Client History</h1>
      <p style={{ color:'#8899AA', fontSize:'0.88rem', marginBottom:28 }}>Search any client to see all their past visits.</p>

      <div className="admin-analytics-2" style={{ display:'grid', gridTemplateColumns:'320px 1fr', gap:20, alignItems:'flex-start' }}>

        {/* Client list */}
        <div style={{ background:'white', borderRadius:10, border:'1px solid rgba(26,68,128,0.08)', overflow:'hidden' }}>
          <div style={{ padding:'14px 16px', borderBottom:'1px solid rgba(26,68,128,0.06)' }}>
            <div style={{ position:'relative' }}>
              <div style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#8899AA' }}><SearchIcon s={15}/></div>
              <input value={search} onChange={e=>{setSearch(e.target.value);setSelKey(null);}} placeholder="Search by name or phone..."
                style={{ width:'100%', padding:'9px 12px 9px 32px', border:'1px solid rgba(26,68,128,0.15)', borderRadius:6, fontSize:'0.84rem', fontFamily:'Jost,sans-serif', outline:'none', color:'#1C2B3A' }} />
            </div>
          </div>
          <div style={{ maxHeight:500, overflowY:'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding:32, textAlign:'center', color:'#8899AA', fontSize:'0.86rem' }}>No clients found.</div>
            ) : filtered.map(c => {
              const key = (c.phone||'').replace(/\s/g,'')||c.name;
              const isSel = selKey === key;
              return (
                <div key={key} onClick={()=>setSelKey(isSel?null:key)} style={{
                  padding:'13px 16px', cursor:'pointer',
                  background: isSel ? '#F4F7FB' : 'white',
                  borderLeft: isSel ? `3px solid ${C.blue}` : '3px solid transparent',
                  borderBottom: '1px solid rgba(26,68,128,0.05)',
                  transition: 'all 0.15s',
                }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', background: isSel?C.navy:'#F4F7FB', display:'flex', alignItems:'center', justifyContent:'center', color: isSel?'white':C.blue, flexShrink:0 }}><UserIcon s={16}/></div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, color:'#0B1D3A', fontSize:'0.87rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.name}</div>
                      <div style={{ fontSize:'0.74rem', color:'#8899AA' }}>{c.phone||'No phone'}</div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:'0.86rem', fontWeight:700, color:C.navy }}>{c.visits.length}</div>
                      <div style={{ fontSize:'0.66rem', color:'#8899AA' }}>visit{c.visits.length!==1?'s':''}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Client detail */}
        {!sel ? (
          <div style={{ background:'white', borderRadius:10, padding:48, border:'1px solid rgba(26,68,128,0.08)', textAlign:'center', color:'#8899AA' }}>
            <div style={{ fontSize:'2.5rem', marginBottom:12 }}>👤</div>
            <p style={{ fontSize:'0.9rem' }}>Select a client to view their history</p>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div style={{ background:C.navy, borderRadius:10, padding:'20px 24px', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
              <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ width:48, height:48, borderRadius:'50%', background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', color:'white' }}><UserIcon s={22}/></div>
                <div>
                  <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:'1.35rem', fontWeight:700, color:'white' }}>{sel.name}</div>
                  <div style={{ fontSize:'0.8rem', color:'rgba(255,255,255,0.5)' }}>{sel.phone}{sel.email ? ' · '+sel.email : ''}</div>
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {sel.phone && <a href={'tel:'+sel.phone} style={{ padding:'8px 14px', background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:6, color:'white', textDecoration:'none', display:'flex', alignItems:'center', gap:6, fontSize:'0.78rem' }}><PhoneIcon s={14}/> Call</a>}
                {sel.phone && <a href={'https://wa.me/'+waFull+'?text='+waMsg} target="_blank" rel="noreferrer" style={{ padding:'8px 14px', background:'rgba(37,211,102,0.15)', border:'1px solid rgba(37,211,102,0.25)', borderRadius:6, color:'#25D366', textDecoration:'none', display:'flex', alignItems:'center', gap:6, fontSize:'0.78rem' }}><WhatsAppIcon s={14}/> WhatsApp</a>}
              </div>
            </div>

            {/* Stats */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16 }}>
              {[
                ['Total Visits', sel.visits.length, C.navy],
                ['Completed', sel.visits.filter(v=>v.status==='completed').length, '#059669'],
                ['Last Visit', sel.visits[0]?.date ? formatDate(sel.visits[0].date) : 'N/A', C.gold],
              ].map(([label,val,color])=>(
                <div key={label} style={{ background:'white', borderRadius:8, padding:'16px', border:'1px solid rgba(26,68,128,0.08)', textAlign:'center' }}>
                  <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:'1.6rem', fontWeight:700, color, lineHeight:1 }}>{val}</div>
                  <div style={{ fontSize:'0.7rem', color:'#8899AA', marginTop:4, textTransform:'uppercase', letterSpacing:'0.08em' }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Visit history */}
            <div style={{ background:'white', borderRadius:10, border:'1px solid rgba(26,68,128,0.08)', overflow:'hidden' }}>
              <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(26,68,128,0.06)' }}>
                <h3 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:'1.1rem', fontWeight:600, color:'#0B1D3A' }}>Visit History</h3>
              </div>
              {sel.visits.map((v,i) => {
                const sc = ST[v.status||'pending'];
                return (
                  <div key={v.id} style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 20px', borderBottom: i < sel.visits.length-1 ? '1px solid rgba(26,68,128,0.05)' : 'none' }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background: sc.color, flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, color:'#0B1D3A', fontSize:'0.87rem' }}>{v.service}</div>
                      <div style={{ fontSize:'0.76rem', color:'#8899AA' }}>{v.date ? formatDate(v.date) : 'No date'}</div>
                      {v.message && <div style={{ fontSize:'0.75rem', color:'#8899AA', fontStyle:'italic', marginTop:2 }}>"{v.message}"</div>}
                    </div>
                    <span style={{ fontSize:'0.72rem', padding:'3px 10px', borderRadius:12, background:sc.bg, color:sc.color, fontWeight:600, textTransform:'capitalize', flexShrink:0 }}>{v.status||'pending'}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
